import React from "react";
import { GetTopicFilters, TopicAccess, TopicState, TopicType, allTopicAccess, allTopicStates, allTopicTypes, defaultTopicFilter } from "../models";
import { ConnectedProps, connect } from "react-redux";
import { GlobalApplicationState } from "globalApplicationState";
import { Chip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AuthoringSearch from "modules/common/components/filters/authoringSearch";
import FilterContainer from "modules/common/components/filters/filterContainer";
import AuthoringFilter from "modules/common/components/filters/authoringFilter";
import CheckboxDropdown from "./common/checkboxDropdown";
import { SEARCH_HINT_TEXT } from "../styles/constants";

class TopicFilters extends React.Component<PropsWithRedux, ComponentState> {
    constructor(props: PropsWithRedux) {
        super(props);
        this.state = {
            textToSearch: this.props.filters.textToSearch || "",
            lcidToSearch: this.props.filters.lcidToSearch || ""
        };
    }

    private isFilterDisabled = (newFilter?: GetTopicFilters) : boolean => {
        const filters = newFilter ?? (this.props?.filters || defaultTopicFilter);
        const textToSearch = this.state.textToSearch?.trim();

        return textToSearch?.trim().length === 0
            && filters?.access?.length === 0
            && filters?.type?.length === 0
            && filters?.state?.length === 0;
    };

    public render() {
        return (
            <FilterContainer
                filterSelection={this.getFilterSelection()}
                filterCommands={
                    <AuthoringSearch
                        canSearch={!this.isFilterDisabled()}
                        onApplyFilters={this.onChangeTextToSearch}
                        onClearFilters={this.clearFilters}
                    />
                }
                filters={
                    <AuthoringFilter
                        textToSearch={this.state.textToSearch || ""}
                        searchTextPlaceholder={SEARCH_HINT_TEXT}
                        additionalFilters={
                            <React.Fragment>
                                <CheckboxDropdown
                                    label="Access"
                                    options={allTopicAccess}
                                    currentOptions={this.props.filters.access}
                                    onClickOption={this.onUpdateAccess}
                                />
                                <CheckboxDropdown
                                    label="State"
                                    options={allTopicStates}
                                    currentOptions={this.props.filters.state}
                                    onClickOption={this.onUpdateState}
                                />
                                <CheckboxDropdown
                                    label="Types"
                                    options={allTopicTypes}
                                    currentOptions={this.props.filters.type}
                                    onClickOption={this.onUpdateType}
                                />
                            </React.Fragment>
                        }
                        onChangeTextToSearch={this.onChangeTextToSearch}
                        onUpdateTextToSearch={(event) => this.onUpdateTextToSearch(event)}
                    />
                }
                styles={{ marginBottom: "1px" }}
            />
        );
    }

    private clearFilters = () => this.props.onSearch(defaultTopicFilter);

    private onUpdateAccess = (updatedAccess: TopicAccess[]) => {
        const updatedFilters = { ...this.props.filters, access: updatedAccess };
        this.updateFilter(updatedFilters, (updatedAccess?.length ?? 0) === 0);
    };

    private onRemoveAccess = (access: TopicAccess) => {
        const updatedAccess = this.props.filters.access?.filter(current => current !== access);
        this.onUpdateAccess(updatedAccess ?? []);
    };

    private onUpdateState = (updatedState: TopicState[]) => {
        const updatedFilters = { ...this.props.filters, state: updatedState };
        this.updateFilter(updatedFilters, (updatedState?.length ?? 0) === 0);
    };

    private onRemoveState = (state: TopicState) => {
        const updatedStates = this.props.filters.state?.filter(current => current !== state);
        this.onUpdateState(updatedStates ?? []);
    };

    private onUpdateType = (updatedType: TopicType[]) => {
        const updatedFilters = { ...this.props.filters, type: updatedType };
        this.updateFilter(updatedFilters, (updatedType?.length ?? 0) === 0);
    };

    private onRemoveType = (type: TopicType) => {
        const updatedTypes = this.props.filters.type?.filter(current => current !== type);
        this.onUpdateType(updatedTypes ?? []);
    };

    private updateFilter = (newFilter: GetTopicFilters, isDefaultFilter: boolean) => {
        const partialFilter = isDefaultFilter ?
            { ...newFilter, isFilterEnabled: !this.isFilterDisabled(newFilter) } :
            { ...newFilter, isFilterEnabled: true };

        this.props.onSearch(partialFilter);
    };

    private onChangeTextToSearch = () => {
        const text = this.state.textToSearch;
        const partialFilter = { ...this.props.filters, textToSearch: text };
        const filters = text.length !== 0 ?
            { ...partialFilter, isFilterEnabled: true }
            : { ...partialFilter, isFilterEnabled: !this.isFilterDisabled(partialFilter) };

        this.props.onSearch(filters);
    };

    private onUpdateTextToSearch = (event) => {
        const valueToSearch = event.target.value || "";
        this.setState({ textToSearch: valueToSearch });
    };

    private getFilterSelection = (): JSX.Element | undefined => {
        const { filters } = this.props;

        if (!filters?.textToSearch && !filters.type?.length && !filters.state?.length && !filters.access?.length)
            return undefined;

        return (
            <React.Fragment>
                {!!filters.textToSearch &&
                    <Chip
                        key="search-text"
                        label={`"${filters.textToSearch}"`}
                        onDelete={() => {
                            this.setState({ textToSearch: "" }, () => this.onChangeTextToSearch());
                        }}
                        deleteIcon={<CloseIcon />}
                    />
                }
                {filters.type?.map((type) =>
                    <Chip
                        key={type}
                        label={type}
                        onDelete={() => this.onRemoveType(type)}
                        deleteIcon={<CloseIcon />}
                    />
                )}
                {filters.state?.map((state) =>
                    <Chip
                        key={state}
                        label={state}
                        onDelete={() => this.onRemoveState(state)}
                        deleteIcon={<CloseIcon />}
                    />
                )}
                {filters.access?.map((access) =>
                    <Chip
                        key={access}
                        label={access}
                        onDelete={() => this.onRemoveAccess(access)}
                        deleteIcon={<CloseIcon />}
                    />
                )}
            </React.Fragment>
        );
    };
}

interface ComponentProps {
    filters: GetTopicFilters;
    onSearch: (filters: GetTopicFilters) => void;
}

interface ComponentState {
    lcidToSearch: string;
    textToSearch: string;
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: ComponentProps) => ({
        ...ownProps,
    })
);

type PropsWithRedux = ConnectedProps<typeof connector>;

export default connector(TopicFilters);